/**
 * TodoLinkChip.jsx: the Job, Quote, Invoice or Person a to-do (or a suggestion)
 * points at. Icon per type plus the label the server sent.
 *
 *  - canOpen: click opens the record (crossNav helpers)
 *  - not canOpen: a lock, tooltip "No access", not clickable (the label is the
 *    server's snapshot, so nothing about the record leaks)
 *  - missing: "(deleted)", not clickable
 * A chip that can't open lets the click through to its row.
 *
 * The chip shows record text, so it always carries a static data-track
 * (`trackKey`): click capture must never record the label.
 */
import { useNavigate } from 'react-router-dom';
import { Chip, Tooltip } from '@mui/material';
import {
  Work as JobIcon,
  Description as QuoteIcon,
  AttachMoney as InvoiceIcon,
  Person as PersonIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { navigateToJob, navigateToQuote, navigateToInvoice, navigateToContact } from '../../lib/crossNav';

const TYPE = {
  job: { icon: JobIcon, open: navigateToJob },
  quote: { icon: QuoteIcon, open: navigateToQuote },
  invoice: { icon: InvoiceIcon, open: navigateToInvoice },
  contact: { icon: PersonIcon, open: navigateToContact },
};

export default function TodoLinkChip({ link, trackKey = 'todos.link', size = 'small', sx }) {
  const navigate = useNavigate();
  if (!link?.type) return null;
  const t = TYPE[link.type] || TYPE.job;
  const missing = !!link.missing;
  const canOpen = !missing && link.canOpen !== false;
  const Icon = canOpen || missing ? t.icon : LockIcon;
  const label = [link.label || '(No name)', link.sublabel].filter(Boolean).join(' · ') + (missing ? ' (deleted)' : '');

  const chip = (
    <Chip
      data-track={trackKey}
      size={size}
      variant="outlined"
      icon={<Icon sx={{ fontSize: '14px !important' }} />}
      label={label}
      clickable={canOpen}
      onClick={canOpen ? (e) => { e.stopPropagation(); t.open(navigate, link.id); } : undefined}
      sx={{
        maxWidth: '100%',
        height: { xs: 28, sm: 22 },
        fontSize: '0.7rem',
        color: missing ? 'text.disabled' : 'text.secondary',
        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
        // Phones: a 44px tall tap area around the 28px chip. The margin keeps that
        // room clear, so it never lies over the title or the line above or below.
        ...(canOpen && {
          position: 'relative',
          my: { xs: 1, sm: 0 },
          '&::after': { content: '""', position: 'absolute', left: 0, right: 0, top: { xs: -8, sm: 0 }, bottom: { xs: -8, sm: 0 } },
        }),
        ...sx,
      }}
    />
  );
  if (canOpen || missing) return chip;
  return (
    <Tooltip title="No access">
      <span style={{ display: 'inline-flex', maxWidth: '100%', minWidth: 0 }}>{chip}</span>
    </Tooltip>
  );
}
